import { IApplicationState } from '@spinwise/spinwise-core';
import { useRouter } from 'next/router';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getClientCookie } from 'Utils/cookies';

export const PostHogProviderWrapper = ({ children }: PropsWithChildren) => {
  const posthog = usePostHog();
  const router = useRouter();
  const id = useSelector((state: IApplicationState) => state.accountReducer.info?.id);
  const name = useSelector((state: IApplicationState) => state.accountReducer.info?.name);
  const surname = useSelector((state: IApplicationState) => state.accountReducer.info?.surname);
  const email = useSelector((state: IApplicationState) => state.accountReducer.info?.email);

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY || !posthog) return undefined;

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
      person_profiles: 'always', // or 'always' to create profiles for anonymous users as well
      // Enable debug mode in development
      loaded: thePosthog => {
        if (process.env.NODE_ENV === 'development') thePosthog.debug();
      },
      session_recording: {
        // WARNING: Only enable this if you understand the security implications
        recordCrossOriginIframes: true,
        captureCanvas: {
          recordCanvas: true,
          // accepts a number between 0 and 12
          // if not set locally, when canvas recording is enabled, remote config sets this to 4
          canvasFps: 8,
          // accepts a decimal number between 0 and 1 as a string
          // if not set locally, when canvas recording is enabled, remote config sets this to "0.4"
          canvasQuality: '0.1',
        },
      },
    });
    const handleRouteChange = () => posthog?.capture('$pageview');

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [posthog, router.events]);

  useEffect(() => {
    if (!posthog) return;

    const country = getClientCookie('country');
    if (id) {
      posthog.identify(id, {
        name: `${name} ${surname}`,
        email: `${email}`,
        country,
        id,
      });
      return;
    }

    posthog.identify(posthog.get_distinct_id(), {
      country,
    });
  }, [email, id, name, posthog, surname]);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};
